import React, { Fragment, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFAQById, createFAQ, updateFAQ } from '../../actions/faq';
import { 
  ArrowLeft,
  Save, 
  Plus,
  Trash2,
  HelpCircle,
  Tag,
  AlertTriangle
} from 'lucide-react';

const FAQForm = ({ 
  getFAQById, 
  createFAQ, 
  updateFAQ, 
  faq: { faq, loading }
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    category: [],
    active: true
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);

  useEffect(() => {
    document.title = isEdit ? 'Edit FAQ' : 'Create FAQ';
    if (isEdit && id) {
      getFAQById(id);
    }
  }, [getFAQById, id, isEdit]);

  useEffect(() => {
    if (isEdit && faq) {
      setFormData({
        category: faq.category || [],
        active: faq.active !== undefined ? faq.active : true
      });
    }
  }, [faq, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.category.length === 0) {
      alert('Please add at least one category with questions.');
      return;
    }

    // Validate that each category has at least one FAQ
    const hasEmptyCategories = formData.category.some(cat => !cat.faqs || cat.faqs.length === 0);
    if (hasEmptyCategories) {
      alert('Each category must have at least one question and answer.');
      return;
    }

    if (isEdit) {
      const success = await updateFAQ(id, formData);
      if (success) {
        navigate(`/inventory/v1.2/faqs/${id}`);
      }
    } else {
      const result = await createFAQ(formData, navigate);
      if (result) {
        navigate('/inventory/v1.2/faqs');
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
    });
  };

  const addCategory = () => {
    setFormData({
      ...formData,
      category: [
        ...formData.category,
        {
          name: '',
          faqs: []
        }
      ]
    });
  };

  const updateCategory = (index, field, value) => {
    const updatedCategories = [...formData.category];
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value
    };
    setFormData({
      ...formData,
      category: updatedCategories
    });
  };

  const deleteCategory = (index) => {
    setCategoryToDelete(index);
    setShowDeleteModal(true);
  };

  const confirmDeleteCategory = () => {
    if (categoryToDelete !== null) {
      const updatedCategories = formData.category.filter((_, i) => i !== categoryToDelete);
      setFormData({
        ...formData,
        category: updatedCategories
      });
      setCategoryToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const addFAQ = (categoryIndex) => {
    const updatedCategories = [...formData.category];
    updatedCategories[categoryIndex].faqs = [
      ...(updatedCategories[categoryIndex].faqs || []),
      {
        question: '',
        answer: ''
      }
    ];
    setFormData({
      ...formData,
      category: updatedCategories
    });
  };

  const updateFAQItem = (categoryIndex, faqIndex, field, value) => {
    const updatedCategories = [...formData.category];
    updatedCategories[categoryIndex].faqs[faqIndex] = {
      ...updatedCategories[categoryIndex].faqs[faqIndex],
      [field]: value
    };
    setFormData({
      ...formData,
      category: updatedCategories
    });
  };

  const deleteFAQItem = (categoryIndex, faqIndex) => {
    setFaqToDelete({ categoryIndex, faqIndex });
    setShowDeleteModal(true);
  };

  const confirmDeleteFAQ = () => {
    if (faqToDelete) {
      const updatedCategories = [...formData.category];
      updatedCategories[faqToDelete.categoryIndex].faqs = 
        updatedCategories[faqToDelete.categoryIndex].faqs.filter((_, i) => i !== faqToDelete.faqIndex);
      setFormData({
        ...formData,
        category: updatedCategories
      });
      setFaqToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setCategoryToDelete(null);
    setFaqToDelete(null);
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Link 
              to={isEdit ? `/inventory/v1.2/faqs/${id}` : '/inventory/v1.2/faqs'} 
              className="btn btn-outline-secondary me-3 d-flex align-items-center"
            >
              <ArrowLeft size={18} className="me-2" />
              Back
            </Link>
            <div>
              <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center">
                <HelpCircle size={28} className="me-3 text-primary" />
                {isEdit ? 'Edit FAQ' : 'Create New FAQ'}
              </h1>
              <p className="text-muted">
                {isEdit ? 'Update FAQ content and categories' : 'Add new FAQ content and categories'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Settings */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">Basic Settings</h5>
            </div>
            <div className="card-body">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="active">
                  Active (FAQ will be visible to users)
                </label>
              </div>
            </div>
          </div>

          {/* Categories and FAQs */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="mb-0 d-flex align-items-center">
                <Tag size={20} className="me-2 text-primary" />
                Categories & Questions
              </h5>
              <button
                type="button"
                onClick={addCategory}
                className="btn btn-primary btn-sm d-flex align-items-center"
              >
                <Plus size={16} className="me-2" />
                Add Category
              </button>
            </div>
            <div className="card-body">
              {formData.category.length === 0 ? (
                <div className="alert alert-info text-center">
                  <HelpCircle size={24} className="mb-2" />
                  <p className="mb-0">No categories added yet. Click "Add Category" to get started.</p>
                </div>
              ) : (
                formData.category.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="card border mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1 me-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Category name (e.g., General, Technical, Billing)"
                          value={category.name}
                          onChange={(e) => updateCategory(categoryIndex, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteCategory(categoryIndex)}
                        className="btn btn-outline-danger btn-sm d-flex align-items-center"
                      >
                        <Trash2 size={16} className="me-1" />
                        Delete Category
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Questions & Answers</h6>
                        <button
                          type="button"
                          onClick={() => addFAQ(categoryIndex)}
                          className="btn btn-outline-primary btn-sm d-flex align-items-center"
                        >
                          <Plus size={16} className="me-1" />
                          Add Question
                        </button>
                      </div>
                      
                      {category.faqs && category.faqs.length > 0 ? (
                        category.faqs.map((faqItem, faqIndex) => (
                          <div key={faqIndex} className="card border-0 bg-light mb-3">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0 text-primary">Question {faqIndex + 1}</h6>
                                <button
                                  type="button"
                                  onClick={() => deleteFAQItem(categoryIndex, faqIndex)}
                                  className="btn btn-outline-danger btn-sm"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Question:</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter the question..."
                                  value={faqItem.question}
                                  onChange={(e) => updateFAQItem(categoryIndex, faqIndex, 'question', e.target.value)}
                                  required
                                />
                              </div>
                              <div>
                                <label className="form-label">Answer:</label>
                                <textarea
                                  className="form-control"
                                  rows="3"
                                  placeholder="Enter the answer..."
                                  value={faqItem.answer}
                                  onChange={(e) => updateFAQItem(categoryIndex, faqIndex, 'answer', e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="alert alert-info">
                          <HelpCircle size={16} className="me-2" />
                          No questions in this category yet. Click "Add Question" to get started.
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary btn-lg d-flex align-items-center"
              disabled={formData.category.length === 0}
            >
              <Save size={18} className="me-2" />
              {isEdit ? 'Update FAQ' : 'Create FAQ'}
            </button>
          </div>
        </form>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center text-danger">
                    <AlertTriangle size={24} className="me-2" />
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <div className="mb-3">
                      <Trash2 size={48} className="text-danger" />
                    </div>
                    <h6 className="mb-3">
                      {categoryToDelete !== null ? 'Delete Category?' : 'Delete Question?'}
                    </h6>
                    <p className="text-muted mb-4">
                      This action cannot be undone.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={20} className="me-2" />
                      <small>
                        <strong>Warning:</strong> This will permanently delete the selected item.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={categoryToDelete !== null ? confirmDeleteCategory : confirmDeleteFAQ}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

FAQForm.propTypes = {
  getFAQById: PropTypes.func.isRequired,
  createFAQ: PropTypes.func.isRequired,
  updateFAQ: PropTypes.func.isRequired,
  faq: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  faq: state.faq
});

export default connect(
  mapStateToProps,
  { getFAQById, createFAQ, updateFAQ }
)(FAQForm);
